import { BlueprintFamilyApiItem } from '../../../../api/core'
import languageService from '../../../../services/LanguageService'
import { familyEventTypeMemberships } from './catalogueUtils'
import { getTranslatedName } from './metricUtils'
import { DEFAULT_TAG_GROUP_COLOR, TagGroupInfo } from './tagGroups'

export interface HierarchySibling {
  id: string
  label: string
  // Only set on event-type siblings: the event category the type belongs to, so
  // a caller building a link target can scope both levels in one go.
  categoryId?: string
  // The tag-group color for this sibling's category — its own color for an
  // event category, its parent's for an event type or a blueprint. Same source
  // as the ranked list's `RankBadge` / row color dot, so a sibling menu option
  // and its row in the tree always agree on color. Falls back to
  // `DEFAULT_TAG_GROUP_COLOR` when `tagGroups` is omitted or the id doesn't
  // resolve (orphan data), rather than leaving it undefined and forcing every
  // caller to re-implement the same fallback.
  colorHex: string
}

export interface HierarchySiblings {
  eventCategories: HierarchySibling[]
  // Event types under `categoryId` (all event types when no category is given).
  eventTypes: HierarchySibling[]
  // Blueprints under `eventTypeId`; empty until an event type is known, since
  // the whole catalogue is far too long to offer as a switcher.
  blueprints: HierarchySibling[]
}

const EMPTY_SIBLINGS: HierarchySiblings = { eventCategories: [], eventTypes: [], blueprints: [] }

/**
 * Derives the switchable siblings at each hierarchy level from the catalogue
 * list both the catalogue and the detail page already hold — event categories,
 * the event types inside the current category, and the blueprints under the
 * current event type. Each level is sorted alphabetically by its displayed
 * label.
 *
 * Callers turn these into link targets themselves: where "switch to this
 * sibling" leads differs per page (a filter change on the catalogue, a
 * navigation on a detail page).
 */
export function buildHierarchySiblings(
  blueprints: readonly BlueprintFamilyApiItem[] | undefined,
  options: { categoryId?: string; eventTypeId?: string; userLanguage: string; tagGroups?: TagGroupInfo[] }
): HierarchySiblings {
  if (!blueprints || blueprints.length === 0) return EMPTY_SIBLINGS
  const { categoryId, eventTypeId, userLanguage, tagGroups = [] } = options
  const colorById = new Map(tagGroups.map((group) => [group.id, group.colorHex]))
  const colorFor = (id: string | undefined) => (id && colorById.get(id)) || DEFAULT_TAG_GROUP_COLOR

  // The taxonomy present in the catalogue, collected in one pass; the ids are
  // the taxonomy, so there is nothing else to fetch. An event type keeps the
  // category of the first membership that named it, which is the one the menu
  // link scopes to.
  const categoryIds = new Set<string>()
  const categoryIdByTypeId = new Map<string, string>()
  // Each entry carries the category of the membership that placed it, so a family
  // listed under two types is coloured by the type the menu is currently showing
  // rather than by whichever type happens to be first on the family.
  const blueprintsInType: { blueprint: BlueprintFamilyApiItem; categoryId: string }[] = []

  // A family counts towards every event type it belongs to.
  for (const blueprint of blueprints) {
    for (const membership of familyEventTypeMemberships(blueprint)) {
      const blueprintCategoryId = membership.categoryId
      const blueprintTypeId = membership.typeId

      if (blueprintCategoryId) categoryIds.add(blueprintCategoryId)

      if (blueprintTypeId && blueprintCategoryId && (!categoryId || blueprintCategoryId === categoryId) && !categoryIdByTypeId.has(blueprintTypeId)) {
        categoryIdByTypeId.set(blueprintTypeId, blueprintCategoryId)
      }

      if (eventTypeId && blueprintTypeId === eventTypeId) blueprintsInType.push({ blueprint, categoryId: blueprintCategoryId })
    }
  }

  // A menu is a flat list of names with nothing to read a ranking off, so an
  // analyst scanning it is looking for a name they already have in mind rather
  // than the biggest one. Collate in the user's language: `ja` and `zh` order
  // their own scripts differently from the default.
  const sortByLabel = (a: HierarchySibling, b: HierarchySibling) => a.label.localeCompare(b.label, userLanguage)

  const eventCategories = Array.from(categoryIds)
    .map((id) => ({ id, label: languageService.getTranslation('tagGroups', id) || id, colorHex: colorFor(id) }))
    .sort(sortByLabel)

  const eventTypes = Array.from(categoryIdByTypeId.entries())
    .map(([id, parentId]) => ({
      id,
      label: languageService.getTranslation('tags', id) || id,
      categoryId: parentId,
      colorHex: colorFor(parentId),
    }))
    .sort(sortByLabel)

  const blueprintSiblings = blueprintsInType
    .map(({ blueprint, categoryId: membershipCategoryId }) => ({
      id: blueprint.id,
      label: getTranslatedName(blueprint.names, userLanguage),
      colorHex: colorFor(membershipCategoryId),
    }))
    .sort(sortByLabel)

  return { eventCategories, eventTypes, blueprints: blueprintSiblings }
}
